import type { DbExecutor } from "../executor";
import {
  deriveStairFigures,
  emptyStairsInput,
  validateStairsInput,
  type Stairs,
  type StairsInput,
} from "../../domain/stairs";
import { ValidationError } from "./projectRepository";

/**
 * The dimensions that belong to one placed staircase (spec §60I).
 *
 * The staircase itself is an ordinary design object, so scenarios, lineage,
 * scope binding and Existing/Proposed semantics all behave exactly as they
 * do for a bathtub. This table only holds the handful of stair-specific
 * facts an estimate is priced from — and every one of them may be unknown,
 * because a renovation usually starts before anyone has measured.
 */
interface StairRow {
  object_id: string;
  configuration: string;
  up_direction: string;
  total_rise_x100: number | null;
  riser_count: number | null;
  tread_count: number | null;
  tread_depth_x100: number | null;
  riser_height_x100: number | null;
  has_landing: number;
  handrail: number;
  guardrail: number;
  finish_notes: string;
  notes: string;
}

function mapStairs(row: StairRow): Stairs {
  return {
    objectId: row.object_id,
    configuration: row.configuration as Stairs["configuration"],
    upDirection: row.up_direction === "back" ? "back" : "forward",
    totalRiseX100: row.total_rise_x100 === null ? null : Number(row.total_rise_x100),
    riserCount: row.riser_count === null ? null : Number(row.riser_count),
    treadCount: row.tread_count === null ? null : Number(row.tread_count),
    treadDepthX100: row.tread_depth_x100 === null ? null : Number(row.tread_depth_x100),
    riserHeightX100: row.riser_height_x100 === null ? null : Number(row.riser_height_x100),
    hasLanding: Number(row.has_landing) === 1,
    handrail: Number(row.handrail) === 1,
    guardrail: Number(row.guardrail) === 1,
    finishNotes: row.finish_notes,
    notes: row.notes,
  };
}

const SELECT = `SELECT object_id, configuration, up_direction, total_rise_x100, riser_count, tread_count,
                       tread_depth_x100, riser_height_x100, has_landing, handrail, guardrail, finish_notes, notes
                  FROM plan_stairs`;

export class StairsRepository {
  constructor(private readonly db: DbExecutor) {}

  async get(objectId: string): Promise<Stairs | null> {
    const rows = await this.db.select<StairRow>(`${SELECT} WHERE object_id = ?`, [objectId]);
    return rows.length === 0 ? null : mapStairs(rows[0]);
  }

  /** Every staircase on one design's floor, for the plan symbol and the sheet. */
  async listByDesignFloor(designId: string, floorId: string): Promise<Map<string, Stairs>> {
    const rows = await this.db.select<StairRow>(
      `${SELECT.replace("FROM plan_stairs", "FROM plan_stairs s")}
         WHERE s.object_id IN (SELECT id FROM plan_objects WHERE design_id = ? AND floor_id = ? AND archived_at IS NULL)`,
      [designId, floorId],
    );
    return new Map(rows.map((row) => [row.object_id, mapStairs(row)]));
  }

  /**
   * Record what is known about a staircase. Called when one is placed (with
   * the defaults) and whenever the inspector is edited; the derived figures
   * are filled in but never invented — an unknown stays null.
   */
  async save(objectId: string, input: StairsInput): Promise<Stairs> {
    const problems = validateStairsInput(input);
    if (problems.length > 0) throw new ValidationError(problems);
    const values = deriveStairFigures(input);
    const now = new Date().toISOString();
    const existing = await this.db.select<{ object_id: string }>(
      "SELECT object_id FROM plan_stairs WHERE object_id = ?",
      [objectId],
    );
    const params = [
      values.configuration,
      values.upDirection,
      values.totalRiseX100,
      values.riserCount,
      values.treadCount,
      values.treadDepthX100,
      values.riserHeightX100,
      values.hasLanding ? 1 : 0,
      values.handrail ? 1 : 0,
      values.guardrail ? 1 : 0,
      values.finishNotes,
      values.notes,
      now,
    ];
    if (existing.length === 0) {
      await this.db.execute(
        `INSERT INTO plan_stairs (configuration, up_direction, total_rise_x100, riser_count, tread_count,
           tread_depth_x100, riser_height_x100, has_landing, handrail, guardrail, finish_notes, notes, updated_at, object_id)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [...params, objectId],
      );
    } else {
      await this.db.execute(
        `UPDATE plan_stairs SET configuration = ?, up_direction = ?, total_rise_x100 = ?, riser_count = ?,
           tread_count = ?, tread_depth_x100 = ?, riser_height_x100 = ?, has_landing = ?, handrail = ?,
           guardrail = ?, finish_notes = ?, notes = ?, updated_at = ? WHERE object_id = ?`,
        [...params, objectId],
      );
    }
    return (await this.get(objectId))!;
  }

  /** A newly placed staircase starts with the ordinary defaults. */
  async ensure(objectId: string): Promise<Stairs> {
    const existing = await this.get(objectId);
    return existing ?? this.save(objectId, emptyStairsInput());
  }
}
